import type { Vehicle, VehicleGroup } from "@/types/fleet";
import { hasDisplayablePlate } from "@/lib/fleet/plate-number";

export function getVehiclePlateDisplay(vehicle: Vehicle): string | null {
  const plate = vehicle.plateNumber?.trim();
  if (hasDisplayablePlate(plate)) return plate;
  return null;
}

/** Upstream tracker id, shown ahead of the plate. Blank and "-" both mean "not set". */
export function getVehicleDeviceIdDisplay(vehicle: Vehicle): string | null {
  const deviceId = vehicle.deviceId?.trim();
  if (!deviceId || deviceId === "-") return null;
  return deviceId;
}

export function getVehicleMakeModel(vehicle: Vehicle): string {
  const fromFields = [vehicle.maker, vehicle.model].filter(Boolean).join(" ").trim();
  if (fromFields) return fromFields;
  const plate = vehicle.plateNumber?.trim();
  if (vehicle.name && vehicle.name !== plate && vehicle.name !== "-") {
    return vehicle.name;
  }
  return "";
}

/**
 * Plate first, then make and model (plain text; use VehicleSelectLabel in UI).
 *
 * Deliberately *not* extended with the device id and driver that `VehicleSelectLabel` now shows:
 * this is what {@link buildVehicleSelectSections} sorts on, and leading with the device id would
 * reorder every dropdown by tracker number instead of by plate.
 */
export function formatVehicleSelectLabel(vehicle: Vehicle): string {
  const makeModel = getVehicleMakeModel(vehicle);
  const plate = getVehiclePlateDisplay(vehicle);
  const hasPlate = Boolean(plate);

  if (hasPlate && makeModel) return `${plate!} - ${makeModel}`;
  if (hasPlate) return plate!;
  if (makeModel) return makeModel;
  return vehicle.name || `Vehicle ${vehicle.id}`;
}

export interface VehicleSelectSection {
  id: string;
  name: string;
  vehicles: Vehicle[];
  showGroupLabel: boolean;
}

function sortVehiclesByLabel(vehicles: Vehicle[]): Vehicle[] {
  return [...vehicles].sort((a, b) =>
    formatVehicleSelectLabel(a).localeCompare(formatVehicleSelectLabel(b), undefined, {
      sensitivity: "base",
    }),
  );
}

export function buildVehicleSelectSections(
  vehicles: Vehicle[],
  groups: VehicleGroup[],
  selectedGroupId: string,
): VehicleSelectSection[] {
  const filtered = vehicles.filter(
    (vehicle) => selectedGroupId === "all" || vehicle.groupId === selectedGroupId,
  );
  const sorted = sortVehiclesByLabel(filtered);

  if (selectedGroupId !== "all") {
    return [
      {
        id: selectedGroupId,
        name: "",
        vehicles: sorted,
        showGroupLabel: false,
      },
    ];
  }

  const byGroupId = new Map<string, Vehicle[]>();
  for (const vehicle of sorted) {
    const groupId = vehicle.groupId || "__ungrouped__";
    const bucket = byGroupId.get(groupId);
    if (bucket) {
      bucket.push(vehicle);
    } else {
      byGroupId.set(groupId, [vehicle]);
    }
  }

  const sections: VehicleSelectSection[] = [];
  const usedGroupIds = new Set<string>();

  for (const group of groups) {
    const groupVehicles = byGroupId.get(group.id);
    if (!groupVehicles?.length) continue;
    sections.push({
      id: group.id,
      name: group.name,
      vehicles: groupVehicles,
      showGroupLabel: true,
    });
    usedGroupIds.add(group.id);
  }

  for (const [groupId, groupVehicles] of byGroupId) {
    if (usedGroupIds.has(groupId)) continue;
    sections.push({
      id: groupId,
      name: groupId === "__ungrouped__" ? "—" : (groupVehicles[0]?.groupName ?? groupId),
      vehicles: groupVehicles,
      showGroupLabel: true,
    });
  }

  return sections;
}
